import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ChevronUp, Cpu, FileText, FolderOpen, Languages, Moon, Pause, Play, Sparkles, Sun, Terminal, Wrench, Zap } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { PHASES, type MissionState, type PhaseKey } from "../mission/engine";
import { phaseTitle } from "../mission/lead";
import { LIVE } from "./AgentCard";
import { Counter, Elapsed, compact, useT } from "./kit";

type RailKey = "intake" | PhaseKey;

/** The procedure as the mission runs it: intake, then the five waves. A finished wave can be reopened on the canvas. */
export function ProcessRail({ state, onSelect }: { state?: MissionState; onSelect?: (phase: PhaseKey | null) => void }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const nodes: RailKey[] = ["intake", ...PHASES];
  return (
    <nav aria-label={tr("backoffice:agentic.rail.label")} className="flex min-w-0 items-center overflow-x-auto">
      {nodes.map((key, i) => {
        const st = key === "intake" ? (state ? "done" : "active") : state?.phases[key] ?? "pending";
        const selected = !!state && key !== "intake" && state.viewPhase === key;
        const clickable = !!state && key !== "intake" && st !== "pending";
        return (
          <div key={key} className="flex shrink-0 items-center">
            {i ? <span className="agx-rail-link mx-0.5" /> : null}
            <button type="button" disabled={!clickable} onClick={() => onSelect?.(st === "active" ? null : (key as PhaseKey))} className="agx-rail-node disabled:cursor-default" data-state={st} data-selected={selected}>
              <span className="agx-rail-dot" />
              <span className="whitespace-nowrap">{key === "intake" ? tr("backoffice:agentic.rail.intake") : t(phaseTitle(key))}</span>
            </button>
          </div>
        );
      })}
    </nav>
  );
}

/** The mission's header: the file, the procedure rail, live counters and the run controls. */
export function TopBar({ caseNumber, title, state, theme, onTheme, onExit, onPause, onPlay, onSpeed, onSelectPhase, onOpenFile }: {
  caseNumber?: string; title?: string; state?: MissionState; theme: "dark" | "light"; onTheme: () => void; onExit: () => void; onPause?: () => void; onPlay?: () => void; onSpeed?: (s: number) => void; onSelectPhase?: (p: PhaseKey | null) => void; onOpenFile?: () => void;
}) {
  const { t } = useTranslation();
  const { toggle } = useLocale();
  const agents = state ? Object.values(state.agents) : [];
  const live = agents.filter((a) => LIVE.includes(a.status)).length;
  return (
    <header className="relative z-20 flex h-12 shrink-0 items-center gap-3 border-b border-[color:var(--agx-line)] bg-[color:var(--agx-header)] px-3">
      <button type="button" onClick={onExit} className="agx-btn size-8 shrink-0 p-0" data-tone="ghost" aria-label={t("backoffice:agentic.exit")}><ArrowLeft className="size-4 rtl:rotate-180" /></button>
      <div className="flex min-w-0 shrink-0 items-center gap-2.5">
        <span className="grid size-7 place-items-center rounded-[5px] border border-[color:var(--agx-gold)]/40 bg-[color:var(--agx-gold)]/10 text-[color:var(--agx-gold)]"><Sparkles className="size-3.5" /></span>
        <div className="hidden min-w-0 leading-tight md:block">
          <p className="text-[13px] font-semibold">{t("backoffice:agentic.title")}</p>
          <p className="max-w-[260px] truncate text-[11px] agx-dim"><span className="agx-mono" dir="ltr">{caseNumber ?? t("backoffice:agentic.subtitle")}</span>{title ? ` · ${title}` : ""}</p>
        </div>
      </div>
      <div className="mx-auto min-w-0"><ProcessRail state={state} onSelect={onSelectPhase} /></div>
      {state ? (
        <div className="hidden shrink-0 items-center gap-3 rounded-[5px] border border-[color:var(--agx-line)] agx-sunk px-2.5 py-1 text-[11px] 2xl:flex">
          <Stat icon={Cpu} label={t("backoffice:agentic.stats.agents")}><span className="agx-mono" style={{ color: "var(--agx-cyan)" }}>{live}</span><span className="agx-mono agx-faint">/{agents.length}</span></Stat>
          <Stat icon={Wrench} label={t("backoffice:agentic.stats.tools")}><Counter value={state.stats.tools} /></Stat>
          <Stat icon={FileText} label={t("backoffice:agentic.stats.sources")}><Counter value={state.stats.sources} /></Stat>
          <Stat icon={Zap} label={t("backoffice:agentic.stats.tokens")}><span className="agx-mono">{compact(state.stats.tokens)}</span></Stat>
          <span className="h-4 w-px bg-[color:var(--agx-line-2)]" />
          <Elapsed from={state.startedAt} to={state.endedAt ?? (state.status === "paused" ? state.pausedAt : undefined)} className="text-[12px] text-[color:var(--agx-gold)]" />
        </div>
      ) : null}
      {state && state.status !== "done" && state.status !== "stopped" ? (
        <div className="flex shrink-0 items-center gap-1">
          {state.status === "paused" ? <button type="button" onClick={onPlay} className="agx-btn h-8 px-2.5" aria-label={t("backoffice:agentic.play")}><Play className="size-3.5" /></button>
            : <button type="button" disabled={state.status !== "running"} onClick={onPause} className="agx-btn h-8 px-2.5" aria-label={t("backoffice:agentic.pause")}><Pause className="size-3.5" /></button>}
          <div className="ms-1 flex overflow-hidden rounded-[5px] border border-[color:var(--agx-line-2)]" role="group" aria-label={t("backoffice:agentic.speed")}>
            {[1, 2, 4].map((s) => <button key={s} type="button" onClick={() => onSpeed?.(s)} className={`agx-mono h-8 px-2 text-[11px] ${state.speed === s ? "bg-[color:var(--agx-gold)] text-[color:var(--agx-on-accent)]" : "agx-dim agx-hover"}`}>{s}×</button>)}
          </div>
        </div>
      ) : null}
      {/* The conventional case file, the system of record, is one click away at any point: there every finding can be checked, confirmed or overridden. */}
      {onOpenFile ? (
        <button type="button" className="agx-btn h-8 shrink-0 px-3" data-tone={state?.status === "done" || state?.status === "stopped" ? "primary" : undefined} onClick={onOpenFile} title={t("backoffice:agentic.caseFileHint")}>
          <FolderOpen className="size-3.5" />{t("backoffice:agentic.caseFile")}
        </button>
      ) : null}
      <button type="button" onClick={onTheme} className="agx-btn h-8 shrink-0 px-2.5" aria-label={t(theme === "dark" ? "backoffice:agentic.lightTheme" : "backoffice:agentic.darkTheme")} title={t(theme === "dark" ? "backoffice:agentic.lightTheme" : "backoffice:agentic.darkTheme")}>{theme === "dark" ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}</button>
      <button type="button" onClick={toggle} className="agx-btn h-8 shrink-0 px-2.5" aria-label={t("backoffice:agentic.language")}><Languages className="size-3.5" /></button>
    </header>
  );
}

function Stat({ icon: Icon, label, children }: { icon: typeof Cpu; label: string; children: React.ReactNode }) {
  return <span className="flex items-center gap-1" title={label} aria-label={label}><Icon className="size-3.5 agx-faint" />{children}</span>;
}

const SQUAD_COLOR: Record<string, string> = { documents: "var(--agx-cyan)", legal: "var(--agx-violet)", market: "var(--agx-gold)", intel: "var(--agx-green)", drafting: "var(--agx-pink)", quality: "var(--agx-orange)", runtime: "var(--agx-gold-2)", memory: "var(--agx-memory)" };

/** Every step of every agent as it happens, terminal-style. */
export function LogDock({ state }: { state: MissionState }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const lines = state.log.slice(open ? -120 : -1);
  useEffect(() => { if (open) ref.current?.scrollTo({ top: ref.current.scrollHeight }); }, [open, state.log.length]);
  const name = (id: string) => (id === "lead" ? tr("backoffice:agentic.lead.name") : t(state.agents[id]?.name) || id);
  return (
    <div className="agx-glass relative z-10 shrink-0 rounded-md">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-3 py-1.5 text-[11px]">
        <Terminal className="size-3.5 text-[color:var(--agx-green)]" />
        <span className="font-semibold uppercase tracking-[0.12em] agx-faint">{tr("backoffice:agentic.log")}</span>
        <span className="agx-mono ms-2 min-w-0 flex-1 truncate text-start agx-dim">{!open && lines[0] ? `${name(lines[0].agentId)} · ${t(lines[0].text)}` : ""}</span>
        <span className="agx-mono agx-faint">{state.log.length}</span>
        <ChevronUp className={`size-3.5 agx-faint transition-transform ${open ? "" : "rotate-180"}`} />
      </button>
      {open ? (
        <div ref={ref} className="agx-mono max-h-48 overflow-y-auto border-t border-[color:var(--agx-line)] px-3 py-2 text-[11px] leading-5">
          {lines.map((l) => {
            const agent = state.agents[l.agentId];
            const color = l.agentId === "lead" ? "var(--agx-gold)" : SQUAD_COLOR[agent?.squad ?? "documents"];
            return (
              <div key={l.id} className="flex gap-2">
                <span className="shrink-0 agx-faint" dir="ltr">{new Date(l.at).toLocaleTimeString("en-GB", { hour12: false })}</span>
                <span className="w-44 shrink-0 truncate" style={{ color }}>{name(l.agentId)}</span>
                <span className="w-20 shrink-0 truncate agx-faint">{tr(`backoffice:agentic.logKinds.${l.kind}`)}</span>
                <span className={`min-w-0 flex-1 truncate ${l.kind === "error" ? "text-[color:var(--agx-rose)]" : l.kind === "find" ? "text-[color:var(--agx-text)]" : "agx-dim"}`}>{t(l.text)}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
